import { storeToRefs } from "pinia"
import type { Role } from "~/enums"
import { DeviceRole, FleetRole, UsermanagementRole } from "~/enums"

export function useUserContext(): { user: Ref<UserInfo>; leadRole: Ref<string>; hasRole: (role: Role) => boolean } {
	const userInfoStore = useUserInfoStore()
	const { userInfo } = storeToRefs(userInfoStore)
	const userRoles = computed(() => (userInfo.value ? setRoles(userInfo.value.roles) : []))

	const user = computed<UserInfo>(() => {
		if (userInfo.value) {
			const currUser = {
				...userInfo.value,
				roles: userRoles.value.map((role) => role.name),
				tenant: userInfo.value.tenant ?? userInfo.value?.tenant_list.at(0) ?? "",
				fleet: userInfo.value.fleet
			}
			return currUser as UserInfo
		}
		return {
			sub: "",
			roles: [],
			email: "",
			name: "",
			image: "",
			tenant_list: [],
			tenant: "",
			fleet: undefined
		} as UserInfo
	})

	function hasRole(...roles: Role[]): boolean {
		return userRoles.value.some((userRole: { value: number }) =>
			roles.some(
				(minRole: { value: number }) =>
					((minRole instanceof FleetRole && userRole instanceof FleetRole) ||
						(minRole instanceof DeviceRole && userRole instanceof DeviceRole) ||
						(minRole instanceof UsermanagementRole && userRole instanceof UsermanagementRole)) &&
					userRole.value >= minRole.value
			)
		)
	}

	const leadRole: Ref<string> = computed(() =>
		userRoles ? (userRoles.value.toSorted((roleA: Role, roleB: Role) => (roleA.value < roleB.value ? 1 : -1))[0]?.name ?? "") : ""
	)

	return {
		user,
		leadRole,
		hasRole
	}
}

function setRoles(roles: string[]) {
	return roles
		.map(
			(x) =>
				FleetRole[x as keyof typeof FleetRole] ??
				UsermanagementRole[x as keyof typeof UsermanagementRole] ??
				DeviceRole[x as keyof typeof DeviceRole]
		)
		.filter((x) => !!x)
}
